/**
 * Powersure UI Theme tokens — Feb 2026.
 *
 * Single source of truth voor het Powersure premium dark/emerald systeem.
 * Gebruik deze tokens i.p.v. losse Tailwind-strings zodat de visuele lijn
 * consistent blijft over landing, app, rapport en owner-admin.
 *
 * Conceptbron: owner-screenshots (donker teal/charcoal · bright emerald
 * accenten · witte typografie · card-based · minimalist · lightning-bolt).
 */

export const psColors = {
  // Achtergrond-gradienten (dark premium)
  bgDeep: '#0a1410',           // diepste charcoal/teal
  bgPanel: '#0e1b16',          // panel/card achtergrond
  bgPanelSoft: '#102521',      // iets opgelichte sub-panel
  bgGradient:
    'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)',

  // Emerald accenten
  emerald100: 'rgba(16,185,129,0.10)',
  emerald200: 'rgba(16,185,129,0.20)',
  emerald300: 'rgba(16,185,129,0.30)',
  emerald400: '#34d399',
  emerald500: '#10b981',
  emerald600: '#059669',

  // Tekst
  textPrimary: '#ffffff',
  textSoft: 'rgba(255,255,255,0.78)',
  textMuted: 'rgba(255,255,255,0.58)',
  textDim: 'rgba(255,255,255,0.42)',

  // Borders / glass
  borderSoft: 'rgba(255,255,255,0.10)',
  borderEmerald: 'rgba(52,211,153,0.30)',

  // Status
  statusOk: '#10b981',
  statusWarn: '#f59e0b',
  statusErr: '#ef4444',
  statusInfo: '#38bdf8',
  statusNeutral: '#94a3b8',
};

// Tailwind class-presets — gebruik in className=""
export const psCls = {
  // Volledig dark panel met emerald glow rechtsboven (achtergrond-style nodig)
  surface: 'rounded-3xl border border-white/10 shadow-2xl overflow-hidden relative',

  // Sub-cards binnen surface
  card: 'rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm',
  cardEmerald: 'rounded-xl bg-emerald-500/10 border border-emerald-400/30',
  cardSoft: 'rounded-xl bg-emerald-500/5 border border-emerald-400/20',

  // Typography
  h1: 'text-2xl sm:text-3xl lg:text-4xl font-medium text-white leading-tight',
  h2: 'text-xl sm:text-2xl font-medium text-white leading-tight',
  h3: 'text-base sm:text-lg font-medium text-white',
  body: 'text-sm sm:text-[15px] text-white/75 leading-relaxed',
  bodySmall: 'text-xs text-white/65 leading-snug',
  label: 'text-[11px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold',

  // Iconen-badges
  iconBadgeSm: 'inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0',
  iconBadge: 'inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30',

  // CTAs
  ctaPrimary: 'w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[15px] font-semibold transition-colors shadow-lg shadow-emerald-900/40',
  ctaSecondary: 'w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-white/15 bg-white/5 text-white/90 text-sm font-medium hover:bg-white/10 transition-colors',
};

// Inline-style helper voor surface-achtergrond (Tailwind doet geen radial)
export const psBgStyle = { background: psColors.bgGradient };

// Section-padding presets (responsive)
export const psSection = {
  outer: 'container mx-auto px-4 sm:px-6 lg:px-8',
  vertical: 'py-8 sm:py-12 lg:py-16',
};
